/**
 * LRU (Least Recently Used) Cache Implementation
 * Otomatik olarak en az kullanılan entry'leri siler, memory leak'i önler
 */

interface CacheEntry<T> {
  data: T
  staleTime: number
}

export class LRUCache<T> {
  private readonly cache: Map<string, CacheEntry<T>>
  private readonly maxSize: number

  constructor(maxSize: number = 100) {
    this.cache = new Map()
    this.maxSize = maxSize
  }

  /**
   * Cache'den veri al
   */
  get(key: string): CacheEntry<T> | undefined {
    const entry = this.cache.get(key)
    if (entry) {
      // LRU: Kullanılan entry'yi en sona taşı (most recently used)
      this.cache.delete(key)
      this.cache.set(key, entry)
    }
    return entry
  }

  /**
   * Cache'e veri ekle
   */
  set(key: string, value: CacheEntry<T>): void {
    // Eğer key zaten varsa, önce sil
    if (this.cache.has(key)) {
      this.cache.delete(key)
    }
    // Max size'a ulaşıldıysa, en eski entry'yi sil (least recently used)
    else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      if (firstKey) {
        this.cache.delete(firstKey)
        console.log(`[LRU Cache] Evicted oldest entry: ${firstKey}`)
      }
    }
    // Yeni entry'yi ekle (en sona)
    this.cache.set(key, value)
  }

  /**
   * Cache'den entry sil
   */
  delete(key: string): boolean {
    return this.cache.delete(key)
  }

  /**
   * Cache'i temizle
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Cache size'ı döndür
   */
  size(): number {
    return this.cache.size
  }

  /**
   * Cache'de key var mı kontrol et
   */
  has(key: string): boolean {
    return this.cache.has(key)
  }

  /**
   * Stale entry'leri temizle (TTL kontrolü)
   */
  cleanStaleEntries(cacheTime: number): number {
    const now = Date.now()
    let cleaned = 0
    const keysToDelete: string[] = []

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.staleTime >= cacheTime) {
        keysToDelete.push(key)
      }
    }

    keysToDelete.forEach((key) => {
      this.cache.delete(key)
      cleaned++
    })

    if (cleaned > 0) {
      console.log(`[LRU Cache] Cleaned ${cleaned} stale entries`)
    }

    return cleaned
  }
}
